// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import type { ReleaseIdentityV1 } from './native-release-catalog.types';
import { ReleaseIdentityV1Schema } from './schemas/native-release-catalog.schemas';

export type SambaRuntimeChannel = 'stable' | 'latest';
export type SambaRuntimePlatform = 'darwin' | 'win32';
export type SambaRuntimeArchitecture = 'x64' | 'arm64';
export type SambaRuntimeContractIdentity = string;
export type SambaRuntimeValidationMode = 'run' | 'contract';

export const SambaRuntimeProfileActivation = {
    ACTIVATED: 'activated',
    REVOKED: 'revoked',
} as const;
export type SambaRuntimeProfileActivation =
    (typeof SambaRuntimeProfileActivation)[keyof typeof SambaRuntimeProfileActivation];

export const SambaRuntimeProfileStateFileName = 'profile-state-v1.json';
export const SambaRuntimeProfileHandoffFileName = 'profile-handoff-v1.json';
export const SambaRuntimeProfileDirectoryName = 'browser-generations';
export const SambaRuntimeApplicationMetadataDirectoryName = 'application-generations';

export interface SambaRuntimeProfileStateV1 {
    readonly schemaVersion: 1;
    readonly activeGeneration: string;
    readonly metadataWriterProcessId: number;
}

export interface SambaRuntimeProfileHandoffV1 {
    readonly schemaVersion: 1;
    readonly token: string;
    readonly parentProcessId: number;
    readonly parentGeneration: string;
    readonly frozenGeneration: string | null;
    readonly replacementGeneration: string;
}

export interface SambaRuntimeProfileStateV2 {
    readonly schemaVersion: 2;
    readonly activeGeneration: string;
    readonly activeMetadataGeneration: string | null;
    readonly metadataWriterProcessId: number;
}

export interface SambaRuntimeProfileHandoffV2 {
    readonly schemaVersion: 2;
    readonly token: string;
    readonly parentProcessId: number;
    readonly parentGeneration: string;
    readonly parentMetadataGeneration: string | null;
    readonly frozenGeneration: string | null;
    readonly replacementGeneration: string;
}

export const SambaRuntimeProfileStateV1Schema: z.ZodType<SambaRuntimeProfileStateV1> = z
    .object({
        schemaVersion: z.literal(1),
        activeGeneration: z.string().uuid(),
        metadataWriterProcessId: z.number().int().positive(),
    })
    .strict();

export const SambaRuntimeProfileHandoffV1Schema: z.ZodType<SambaRuntimeProfileHandoffV1> = z
    .object({
        schemaVersion: z.literal(1),
        token: z.string().uuid(),
        parentProcessId: z.number().int().positive(),
        parentGeneration: z.string().uuid(),
        frozenGeneration: z.string().uuid().nullable(),
        replacementGeneration: z.string().uuid(),
    })
    .strict()
    .refine(
        (handoff) =>
            handoff.replacementGeneration !== handoff.parentGeneration &&
            handoff.frozenGeneration !== handoff.parentGeneration &&
            handoff.frozenGeneration !== handoff.replacementGeneration,
        'Replacement and frozen browser generations must differ from the live parent generation'
    );

export const SambaRuntimeProfileStateV2Schema: z.ZodType<SambaRuntimeProfileStateV2> = z
    .object({
        schemaVersion: z.literal(2),
        activeGeneration: z.string().uuid(),
        activeMetadataGeneration: z.string().uuid().nullable(),
        metadataWriterProcessId: z.number().int().positive(),
    })
    .strict();

export const SambaRuntimeProfileHandoffV2Schema: z.ZodType<SambaRuntimeProfileHandoffV2> = z
    .object({
        schemaVersion: z.literal(2),
        token: z.string().uuid(),
        parentProcessId: z.number().int().positive(),
        parentGeneration: z.string().uuid(),
        parentMetadataGeneration: z.string().uuid().nullable(),
        frozenGeneration: z.string().uuid().nullable(),
        replacementGeneration: z.string().uuid(),
    })
    .strict()
    .refine(
        (handoff) =>
            handoff.replacementGeneration !== handoff.parentGeneration &&
            handoff.frozenGeneration !== handoff.parentGeneration &&
            handoff.frozenGeneration !== handoff.replacementGeneration &&
            handoff.parentMetadataGeneration !== handoff.replacementGeneration,
        'Staged browser and metadata generations must differ from their authoritative sources'
    );

export function normalizeSambaRuntimeProfileStateV1(
    value: SambaRuntimeProfileStateV1
): SambaRuntimeProfileStateV2 {
    return { ...value, schemaVersion: 2, activeMetadataGeneration: null };
}

export function normalizeSambaRuntimeProfileHandoffV1(
    value: SambaRuntimeProfileHandoffV1
): SambaRuntimeProfileHandoffV2 {
    return { ...value, schemaVersion: 2, parentMetadataGeneration: null };
}

export function parseSambaRuntimeProfileState(value: unknown): SambaRuntimeProfileStateV2 {
    const { schemaVersion } = z.object({ schemaVersion: z.literal([1, 2]) }).parse(value);
    switch (schemaVersion) {
        case 1:
            return normalizeSambaRuntimeProfileStateV1(
                SambaRuntimeProfileStateV1Schema.parse(value)
            );
        case 2:
            return SambaRuntimeProfileStateV2Schema.parse(value);
    }
}

export function parseSambaRuntimeProfileHandoff(value: unknown): SambaRuntimeProfileHandoffV2 {
    const { schemaVersion } = z.object({ schemaVersion: z.literal([1, 2]) }).parse(value);
    switch (schemaVersion) {
        case 1:
            return normalizeSambaRuntimeProfileHandoffV1(
                SambaRuntimeProfileHandoffV1Schema.parse(value)
            );
        case 2:
            return SambaRuntimeProfileHandoffV2Schema.parse(value);
    }
}

export const SambaRuntimeProfileStateStorageContract = {
    variants: {
        v1: { normalize: normalizeSambaRuntimeProfileStateV1 },
        v2: { normalize: SambaRuntimeProfileStateV2Schema.parse },
    },
    writerVariant: 'v2',
} as const;

export const SambaRuntimeProfileHandoffStorageContract = {
    variants: {
        v1: { normalize: normalizeSambaRuntimeProfileHandoffV1 },
        v2: { normalize: SambaRuntimeProfileHandoffV2Schema.parse },
    },
    writerVariant: 'v2',
} as const;

export interface SambaRuntimePayloadIdentityV1 {
    readonly schemaVersion: 1;
    readonly releaseSequence: string;
    readonly releaseId: string;
    readonly sourceCommit: string;
    readonly sha256: string;
}

export function isSameSambaRuntimePayloadIdentity(
    left: SambaRuntimePayloadIdentityV1,
    right: SambaRuntimePayloadIdentityV1
): boolean {
    return (
        left.schemaVersion === right.schemaVersion &&
        left.releaseSequence === right.releaseSequence &&
        left.releaseId === right.releaseId &&
        left.sourceCommit === right.sourceCommit &&
        left.sha256 === right.sha256
    );
}

export type SambaRuntimeHttpValidationResult = 'not-modified' | 'current' | 'candidate';
export type SambaRuntimeFailurePhase = 'startup' | 'update-check';
export type SambaRuntimeFailureReason = 'runtime-startup-failed' | 'runtime-update-check-failed';
export type SambaRuntimeUpdatePhase =
    | 'idle'
    | 'checking'
    | 'downloading'
    | 'downloaded'
    | 'launching'
    | 'relaunch-pending';

export interface SambaRuntimeHttpValidationV1 {
    readonly checkedAt: string;
    readonly result: SambaRuntimeHttpValidationResult;
    readonly releaseSequence: string;
}

export interface SambaRuntimeFailureV1 {
    readonly occurredAt: string;
    readonly phase: SambaRuntimeFailurePhase;
    readonly reason: SambaRuntimeFailureReason;
}

export interface SambaRuntimeStatusV1 {
    readonly schemaVersion: 1;
    readonly activePayload: SambaRuntimePayloadIdentityV1;
    readonly updatePhase: SambaRuntimeUpdatePhase;
    readonly candidatePayload?: SambaRuntimePayloadIdentityV1;
    readonly previousKnownGoodPayload?: SambaRuntimePayloadIdentityV1;
    readonly lastHttpValidation?: SambaRuntimeHttpValidationV1;
    readonly lastFailure?: SambaRuntimeFailureV1;
}

export interface SambaUpdateStatusResponseV1 {
    readonly schemaVersion: 1;
    readonly nativeIdentity: ReleaseIdentityV1;
    readonly runtimePayload: SambaRuntimePayloadIdentityV1;
}

export type SambaRuntimeRelaunchPhase =
    | 'checking-for-update'
    | 'capturing-workspace'
    | 'starting-replacement-app'
    | 'switching-to-replacement-app'
    | 'restoring-session-and-workspace';

export interface SambaRuntimeRelaunchLifecycle {
    readonly onPhaseStarted: (phase: SambaRuntimeRelaunchPhase) => void;
    readonly captureProgressHandoff: () => SambaRuntimeRelaunchCommand;
}

export interface SambaRuntimeRelaunchCommand {
    readonly command: string;
    readonly args?: unknown;
}

export interface SambaRuntimePayloadHandle {
    readonly isNativeUpdateInProgress: () => boolean;
    readonly getPortalSessionCookieHeader: (url: string) => Promise<string>;
    readonly applySambaUpdateStatus: (
        updateStatusResponse: SambaUpdateStatusResponseV1
    ) => Promise<void>;
}

const CanonicalDecimalStringSchema: z.ZodType<string> = z.string().regex(/^(?:0|[1-9]\d*)$/);
const Sha256Schema: z.ZodType<string> = z.string().regex(/^[a-f0-9]{64}$/);
/** Launcher contract v3: the interface a payload runs under, such as `interface-1.0`. */
export const SambaRuntimeContractIdentitySchema: z.ZodType<SambaRuntimeContractIdentity> = z
    .string()
    .regex(/^interface-(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)$/);
const GitCommitSchema: z.ZodType<string> = z.string().regex(/^[a-f0-9]{40}$/);
const RuntimeIdentifierSchema: z.ZodType<string> = z.string().regex(/^[a-z0-9][a-z0-9._-]*$/);
const IsoTimestampSchema: z.ZodType<string> = z.string().datetime({ offset: true });

export const SambaRuntimeChannelSchema: z.ZodType<SambaRuntimeChannel> = z.enum([
    'stable',
    'latest',
]);
export const SambaRuntimePlatformSchema: z.ZodType<SambaRuntimePlatform> = z.enum([
    'darwin',
    'win32',
]);
export const SambaRuntimeArchitectureSchema: z.ZodType<SambaRuntimeArchitecture> = z.enum([
    'x64',
    'arm64',
]);

export const SambaRuntimePayloadIdentityV1Schema: z.ZodType<SambaRuntimePayloadIdentityV1> = z
    .object({
        schemaVersion: z.literal(1),
        releaseSequence: CanonicalDecimalStringSchema,
        releaseId: RuntimeIdentifierSchema,
        sourceCommit: GitCommitSchema,
        sha256: Sha256Schema,
    })
    .strict();

export const SambaRuntimeHttpValidationV1Schema: z.ZodType<SambaRuntimeHttpValidationV1> = z
    .object({
        checkedAt: IsoTimestampSchema,
        result: z.enum(['not-modified', 'current', 'candidate']),
        releaseSequence: CanonicalDecimalStringSchema,
    })
    .strict();

export const SambaRuntimeFailureV1Schema: z.ZodType<SambaRuntimeFailureV1> = z
    .object({
        occurredAt: IsoTimestampSchema,
        phase: z.enum(['startup', 'update-check']),
        reason: z.enum(['runtime-startup-failed', 'runtime-update-check-failed']),
    })
    .strict();

export const SambaRuntimeStatusV1Schema: z.ZodType<SambaRuntimeStatusV1> = z
    .object({
        schemaVersion: z.literal(1),
        activePayload: SambaRuntimePayloadIdentityV1Schema,
        updatePhase: z.enum([
            'idle',
            'checking',
            'downloading',
            'downloaded',
            'launching',
            'relaunch-pending',
        ]),
        candidatePayload: SambaRuntimePayloadIdentityV1Schema.optional(),
        previousKnownGoodPayload: SambaRuntimePayloadIdentityV1Schema.optional(),
        lastHttpValidation: SambaRuntimeHttpValidationV1Schema.optional(),
        lastFailure: SambaRuntimeFailureV1Schema.optional(),
    })
    .strict();

export const SambaUpdateStatusResponseV1Schema: z.ZodType<SambaUpdateStatusResponseV1> = z
    .object({
        schemaVersion: z.literal(1),
        nativeIdentity: ReleaseIdentityV1Schema,
        runtimePayload: SambaRuntimePayloadIdentityV1Schema,
    })
    .strict();

export const SambaRuntimeStatusContract = {
    variants: {
        v1: { normalize: SambaRuntimeStatusV1Schema.parse },
    },
    writerVariant: 'v1',
} as const;

export const SambaUpdateStatusResponseContract = {
    variants: {
        v1: { normalize: SambaUpdateStatusResponseV1Schema.parse },
    },
    writerVariant: 'v1',
} as const;
